// Pure helpers for the INSERT structured-tail editors (§2.4 S6b) — kept out of
// the card component files to stay under the ~200-line budget (extraction of
// OnConflictCard / ReturningCard logic, same split as insertColumnMapHelpers).

import type { VqeEditContext } from '../../edit/editContext';
import type {
  OnConflictSpec,
  QueryStructure,
} from '../../../../sql-resolver/types/queryStructure';
import type { CommandPickerItem } from '../common/CommandPickerPopover';
import { safeExtractStructure } from './insertColumnMapHelpers';
import type { QualifiedName } from '../../../../sql-grammar/model';
import { lookupRelationColumns } from '../../../../sql-resolver/service/snapshotRelations';

/**
 * Head of the ON CONFLICT clause for the collapsed summary line — everything
 * from `ON` up to (not including) the action keyword, e.g. `ON CONFLICT (id)`.
 */
export function conflictSummary(bufferText: string, oc: OnConflictSpec): string {
  if (!oc.range) return 'ON CONFLICT';
  const headEnd = oc.actionRange ? oc.actionRange.startOffset : oc.range.endOffset;
  return bufferText.slice(oc.range.startOffset, headEnd).trim();
}

/**
 * Buffer span of the DO UPDATE SET assignment list (first assignment start →
 * last assignment end), or null when the action carries none (DO NOTHING).
 */
export function conflictAssignmentsSpan(
  oc: OnConflictSpec,
): { readonly start: number; readonly end: number } | null {
  const assigns = oc.updateAssignments ?? [];
  if (assigns.length === 0) return null;
  return {
    start: assigns[0].range.startOffset,
    end: assigns[assigns.length - 1].range.endOffset,
  };
}

function insertTargetRelation(structure: QueryStructure | undefined): QualifiedName | undefined {
  const target = structure?.insertTarget;
  return target && target.target.kind === 'relation'
    ? target.target.qualified
    : undefined;
}

export type ConflictTargetCandidates = {
  /** Every column of the INSERT target table, snapshot order. */
  readonly items: readonly CommandPickerItem[];
  /** foldedName → name-as-written, for composing `columnsText`. */
  readonly nameByKey: ReadonlyMap<string, string>;
  /** foldedNames currently in the conflict target (pre-checked). */
  readonly currentKeys: ReadonlySet<string>;
};

/**
 * Candidates for the conflict-target picker: ALL target-table columns (a
 * conflict target is a multi-select, so listed ≠ excluded), with the current
 * target's columns pre-checked. Empty items ⇒ picker disabled (no snapshot /
 * unresolvable target table).
 */
export function computeConflictTargetCandidates(
  editContext: VqeEditContext,
  structure: QueryStructure | undefined,
  oc: OnConflictSpec,
): ConflictTargetCandidates {
  const snapshot = editContext.service.getSchemaSnapshot();
  const target = insertTargetRelation(structure);
  if (!snapshot || !target) {
    return { items: [], nameByKey: new Map(), currentKeys: new Set() };
  }
  const cols = lookupRelationColumns(snapshot, target) ?? [];
  return {
    items: cols.map((c) => ({ key: c.qualified.foldedName, label: c.qualified.name })),
    nameByKey: new Map(cols.map((c) => [c.qualified.foldedName, c.qualified.name])),
    currentKeys: new Set((oc.target ?? []).map((t) => t.qualified.foldedName)),
  };
}

export type ReturningCandidates = {
  readonly items: readonly CommandPickerItem[];
  readonly textByKey: ReadonlyMap<string, string>;
  /**
   * True when the target table resolved to ≥1 known column and every one of
   * them is already returned — i.e. an empty `items` means exhaustion, not a
   * schema gap. Drives the disabled add-trigger's why-tooltip: exhaustion gets
   * the specific "already returned" reason; a schema gap keeps `noSnapshot`.
   */
  readonly allReturned: boolean;
};

/**
 * Candidates for the RETURNING add-picker: target-table columns (from the
 * schema snapshot) minus expressions already returned, shaped for the
 * CommandPickerPopover wiring.
 */
export function computeReturningCandidates(editContext: VqeEditContext): ReturningCandidates {
  const snapshot = editContext.service.getSchemaSnapshot();
  const structure = snapshot ? safeExtractStructure(editContext) : undefined;
  const target = insertTargetRelation(structure);
  if (!snapshot || !structure || !target) {
    return { items: [], textByKey: new Map(), allReturned: false };
  }
  const existing = new Set(
    (structure.returning ?? []).map((item) => item.expressionText.trim().toLowerCase()),
  );
  const known = lookupRelationColumns(snapshot, target) ?? [];
  const candidates = known.filter((c) => !existing.has(c.qualified.foldedName));
  return {
    items: candidates.map((c) => ({ key: c.qualified.foldedName, label: c.qualified.name })),
    textByKey: new Map(candidates.map((c) => [c.qualified.foldedName, c.qualified.name])),
    allReturned: known.length > 0 && candidates.length === 0,
  };
}
